<template>
  <div class="__BillingReport_Header">
    <!-- Header bottom-->
    <div class="__BillingReport_Header_Content">
      <div class="__BillingReport_Header_Content_Selector">
        <!--Month selector / Report download button -->
        <DateRange
          class="mr-md"
          ref="data-range"
          :range="dateRange"
          :quick-range-options="quickRangeOptions"
          :min-date="minDate"
          :max-date="maxDate"
          @change="onChangeDateRange"
          v-if="vendor === 'aws'"
        />
        <CSelector
          width="200px"
          v-model="currentMonth"
          :options="monthOptions"
          emit-value
          map-options
          :use-input="false"
          v-else
        />

        <CButton
          icon="get_app"
          round
          flat
          :disable="reportCsvUrl === 'unavailable' || !isDownloadable"
          :color="isDownloadable || downloadRequest ? 'primary' : 'muted'"
          :loading="downloadRequest"
          :tooltip="$t('Download the report (.csv)')"
          @click="handleDownloadCsv"
        />
      </div>

      <!--Amount-->
      <div class="__BillingReport_Header_Content_TotalAmount">
        <CLabelValue :label="$t('Monthly total cost')" align="right" small-label>
          <div class="text-lg">
            <CCurrency
              :value="totalAmount"
              :type="currencyType"
              :loading="loading"
              :tooltip="`${currencySymbol(currencyType)}${totalAmount}`"
            />
          </div>
        </CLabelValue>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import CTitle from '@/components/uikit/CTitle';
import CButton from '@/components/uikit/CButton';
import CLabelValue from '@/components/uikit/CLabelValue';
import CSelector from '@/components/uikit/CSelector';
import CCurrency from '@/components/uikit/CCurrency';
import DateRange from '@/components/CustomCharts/DateRange';
import downloader from '@/lib/downloader';
import moment from 'moment';
import { saveAs } from 'file-saver';

const messages = {
  messages: {
    en: {
      Report: 'Report',
      'Monthly total cost': 'Total Cost',
      'Usage report by month and service': 'Usage report by month and service',
      'Download the report (.csv)': 'Download the report (.csv)',
    },
    ja: {
      Report: 'レポート',
      'Usage report by month and service': '月別およびサービス別の利用レポート',
      'Monthly total cost': '当月の合計金額',
      'Download the report (.csv)': 'レポートデータ（.csv）のダウンロード',
    },
  },
};

export default Vue.extend({
  name: 'BillingReportHeader',
  i18n: messages,
  components: { CTitle, CButton, CLabelValue, CSelector, CCurrency, DateRange },
  props: {
    value: {
      type: String,
    },
    data: {
      type: Array,
    },
    owner: {
      type: String,
    },
    loading: Boolean,
    reportHash: {
      type: String,
    },
    dateRange: {
      type: Object,
    },
    minDate: {
      type: Date,
      required: true,
    },
    maxDate: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      quickRangeOptions: ['mtd', 'lastMonth', 'ytd', '3months', '6months', '1year'],
    };
  },
  mounted() {
    this.currentMonth =
      this.vendor === 'azure'
        ? moment().format('YYYY-MM')
        : this.$store.state.userState.dataReady.report;
  },
  computed: {
    ...mapState({
      vendor: state => state.vendor,
    }),

    ...mapState('billingReports', {
      reportCsv: state => state.reportCsv,
      reportRequests: state => state.requests,
    }),

    ...mapState('user', {
      resourceType: state => state.data.resource_type,
    }),
    ...mapState('usageReports', {
      selectedType: state => state.groupBy,
      id: state => state.id,
    }),
    ...mapState('accounts', {
      selectedId: state => state.selectedId,
    }),

    ...mapGetters('crossTags', ['getCrossTagDetailTags']),
    ...mapGetters('user', ['isAccessGroup']),
    ...mapGetters('accounts', ['getAccounts']),

    tags() {
      return this.getCrossTagDetailTags;
    },

    currentMonth: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    monthOptions() {
      if (this.vendor === 'azure') {
        return this.$store.getters['monthOptions'](true);
      } else {
        return this.$store.getters['monthOptions'](false);
      }
    },

    reportCsvUrl() {
      return this.reportCsv[this.reportHash];
    },

    reportRange() {
      const bs = this.reportHash;
      var fromto = bs.split('-');
      var csvfrom = fromto[3] + fromto[4] + fromto[5] + '-' + fromto[6] + fromto[7] + fromto[8];
      return csvfrom;
    },
    downloadRequest() {
      const target = this.reportRequests[this.reportHash];
      return target ? target.inProgress : false;
    },

    isDownloadable() {
      const url = this.reportCsv[this.reportHash];
      if (url && url !== 'unavailable') {
        return true;
      }

      if (this.data.length > 0) {
        return true;
      }

      return false;
    },

    totalAmount() {
      let total = 0;
      this.data.forEach(data => {
        total += data.total;
      });
      return total;
    },

    currencyType() {
      return this.vendor === 'aws' || this.vendor === 'azure' ? 'usd' : 'jpy';
    },
  },
  methods: {
    handleDownloadCsv() {
      if (this.reportCsvUrl) {
        downloader(this.reportCsvUrl);
        return;
      }

      // Generate the CSV data.
      if (this.data.length > 0) {
        let csvData = [];
        let fileName = '';
        if (
          this.resourceType === 'tag' ||
          (this.isAccessGroup && this.selectedType == 'cross_tag')
        ) {
          const convertedData = this.convertAWSCrossTagReportCSV();
          csvData = convertedData.csvData;
          fileName = convertedData.fileName;
        } else {
          switch (this.vendor) {
            case 'aws': {
              const convertedData = this.convertAWSAccountReportCSV();
              csvData = convertedData.csvData;
              fileName = convertedData.fileName;
              break;
            }
            case 'azure': {
              const convertedData = this.convertAzureAccountReportCSV();
              csvData = convertedData.csvData;
              fileName = convertedData.fileName;
              break;
            }
          }
        }

        // Convert CSV data to String.
        var csvText = '';
        for (const item of csvData) {
          csvText += this.objectToCSVRow(item);
        }

        // Save file to local machine.
        var blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, fileName, { autoBom: true });
      }
    },

    convertAWSCrossTagReportCSV() {
      var tagName = '';
      if (this.tags.length > 0) {
        tagName = `${this.tags[0].key}|${this.tags[0].value}`;
      }
      const csvData = [
        [
          'TagName',
          'ServiceCode',
          'CostBeforeTax',
          'Region',
          'UsageQuantity',
          'InstanceType',
          'Operation',
          'ItemDescription',
          'AccountId',
        ],
      ];
      const fileName = `${tagName}-${this.reportRange}-report.csv`;
      for (const item of this.data) {
        const serviceCode = item.service;
        for (const report of item.report) {
          csvData.push([
            tagName,
            serviceCode,
            report.cost,
            report.region,
            report.usage_quantity,
            report.instance_type,
            report.operation,
            report.description,
            report.account,
          ]);
        }
      }
      return { csvData, fileName };
    },

    convertAWSAccountReportCSV() {
      const accountId = this.selectedId;
      let csvData = [
        [
          'AccountId',
          'ServiceCode',
          'CostBeforeTax',
          'Region',
          'UsageQuantity',
          'InstanceType',
          'Operation',
          'ItemDescription',
        ],
      ];
      const fileName = `${accountId}-${this.reportRange}-report.csv`;
      for (const item of this.data) {
        const serviceCode = item.service;
        for (const report of item.report) {
          csvData.push([
            accountId,
            serviceCode,
            report.cost,
            report.region,
            report.usage_quantity,
            report.instance_type,
            report.operation,
            report.description,
          ]);
        }
      }
      return { csvData, fileName };
    },

    convertAzureAccountReportCSV() {
      // Handle account ID mapping to get Azure Customer ID and Subscription ID.
      const accountMap = {};
      const accounts = this.getAccounts;
      for (const account of accounts) {
        // Handle Azure subscription group.
        if (account.is_subscription) {
          for (const rippleCusId in account) {
            const rippleCusObj = account[rippleCusId];
            accountMap[rippleCusId] = {
              azure_customer_id:
                rippleCusObj && rippleCusObj.azure_customer_id
                  ? rippleCusObj.azure_customer_id
                  : '',
              azure_plan_id: account.account_id,
            };
          }
          continue;
        }

        // Handle Azure account group.
        for (const rippleCusId in account) {
          const rippleCusObj = account[rippleCusId];
          accountMap[rippleCusId] = {
            azure_customer_id: account.account_id,
            azure_plan_id:
              rippleCusObj && rippleCusObj.azure_plan_id ? rippleCusObj.azure_plan_id : '',
          };
        }
      }
      const accountId = this.id;
      let csvData = [
        [
          'Customer ID',
          'Subscription ID',
          'Date',
          'Instance Type (VMs Only)',
          'Region',
          'Service',
          'Usage Type',
          'Operation',
          'Description',
          'Quantity',
          'Rate',
          'Cost',
          'Currency',
        ],
      ];
      const fileName = `${accountId}-${this.reportRange}-report.csv`;
      for (const item of this.data) {
        for (const report of item.report) {
          const account = accountMap[report.account];
          let cusId = '';
          let subId = '';
          if (account) {
            cusId = account.azure_customer_id;
            subId = account.azure_plan_id;
          }
          csvData.push([
            cusId,
            subId,
            report.date,
            report.instance_type,
            report.region,
            report.service_name,
            report.usage_type,
            report.operation,
            report.description,
            report.usage_quantity,
            report.unit_price,
            report.cost,
            report.currency,
          ]);
        }
      }
      return { csvData, fileName };
    },

    objectToCSVRow(dataObject) {
      var dataArray = new Array();
      for (var key in dataObject) {
        var innerValue = dataObject[key] ? dataObject[key].toString() : '';
        var result = innerValue.replace(/"/g, '""');
        result = '"' + result + '"';
        dataArray.push(result);
      }
      return dataArray.join(',') + '\r\n';
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'usd':
          return '$';
        case 'jpy':
          return '¥';
        default:
          return '';
      }
    },

    onChangeDateRange(range) {
      this.$emit('update-date-range', range);
    },
  },
});
</script>

<style lang="scss" scoped>
.__BillingReport_Header {
  padding-bottom: $space-sm;
  margin-bottom: $space-sm;
  border-bottom: 1px solid $border-color;

  &_Content {
    display: flex;
    justify-content: space-between;

    &_Actions {
      min-width: 80px;
    }
  }

  &_Content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &_Selector {
      display: flex;
    }

    &_TotalAmount {
      flex-basis: auto;
      text-align: right;
    }
  }
}
</style>
